import InjectSearchOverlay from '@components/SearchInjection';
import {CATEGORY_ICONS_LINKS} from '@constants';
import {CourseCategory} from '@jobseeker/common-core/types';
import {mapCourseCategoryDataToCourse} from '@jobseeker/feature-course/utils';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {cleanUrl} from '@jobseeker/lotus/utils';
import {Link, useTheme} from '@mui/material';
import {Box, Card, Grid, Typography} from '@mui/material';
import dynamic from 'next/dynamic';
import {Image} from '@jobseeker/lotus/components/Image';
import {memo} from 'react';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME;
const isAppIimjobs = PLATFORM === 'iimjobs';
const isAppHirist = PLATFORM === 'hirist';

const CourseList = dynamic(
  () => import('@jobseeker/lotus/components/JobList/V2')
);

const HiristCourseFeed = dynamic(
  () => import('@jobseeker/feature-course/components/HiristCourseFeed')
);

const AdsPanelHarness = memo(
  dynamic(
    () => import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
    {
      ssr: false,
    }
  )
);

type ListProps = {
  isSearchEnabled: boolean;
  allCategoriesCourses: CourseCategory[];
  handleEnquiryInLoggedOutState: () => void;
  isDeviceLarge: boolean;
  tabsList?: {
    dataId: string;
    label: string;
    tag: string;
  }[];
};

const List = (props: ListProps) => {
  const {
    isSearchEnabled,
    allCategoriesCourses,
    handleEnquiryInLoggedOutState,
    isDeviceLarge,
    tabsList,
  } = props;

  const theme = useTheme();

  return (
    <Grid container columnSpacing={2}>
      <Grid item xxs={12} md={isAppIimjobs ? 9 : 12}>
        <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
        {allCategoriesCourses?.map(category => {
          if (category?.data?.length === 0) {
            return <></>;
          }

          const {label, tag, dataId} = (isAppIimjobs
            ? tabsList?.find(data => data.dataId === category.id.toString())
            : {
                label: category.name,
                tag: category.name,
                dataId: category.id,
              }) ?? {label: '', tag: '', dataId: ''};

          const linkToMoreCoursesForThisCategory = `/learning/course/${cleanUrl(
            tag
          )}-course-${dataId}`;

          return (
            <Card
              key={category.id}
              sx={{
                boxShadow: 2,
                borderRadius: 4,
                mx: 0,
                mt: 2,
                pt: 2,
                ...(isAppHirist && {
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  maxWidth: 1024,
                }),
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  mb={isAppHirist ? 2 : 0}
                >
                  {isAppHirist && (
                    <Image
                      src={CATEGORY_ICONS_LINKS[category.id]}
                      alt={label}
                      width={35}
                      height={35}
                      style={{
                        marginLeft: '16px',
                      }}
                    />
                  )}
                  <Typography
                    component="h3"
                    fontSize={theme.typography.pxToRem(20)}
                    fontWeight="bold"
                    pl={2}
                    mb={isAppIimjobs ? 1 : 0}
                  >
                    {label}
                  </Typography>
                </Box>
                <Typography
                  fontSize={theme.typography.pxToRem(14)}
                  mb={isAppHirist ? 2 : 0}
                >
                  <Link
                    href={linkToMoreCoursesForThisCategory}
                    sx={{
                      pr: isAppHirist ? {xxs: 2.5, md: 6} : 2,
                      textDecoration: 'none',
                      ':hover': {
                        color: theme.palette.primary.main,
                      },
                    }}
                    target="_blank"
                  >
                    View more courses
                  </Link>
                </Typography>
              </Box>
              {isAppIimjobs &&
                category.data?.map(({institute, title, Location, id, logo}) => (
                  <CourseList
                    key={id}
                    title={title}
                    variant={VersionVariants.VERSION_TWO}
                    location={Location || 'Anywhere'}
                    companyName={institute}
                    experiance={institute}
                    date={Date.now()}
                    refCode=""
                    companyImgUrl={logo}
                    showPosted={false}
                    isCompanyJob={false}
                    isPremiumJob={false}
                    isSaved={false}
                    showSaveIcon={false}
                    redirectionLink={
                      new URL(
                        `/course/${cleanUrl(
                          title
                        )}-${id}?ref=lc&reforg=learning&refinv=learning`,
                        process.env.NEXT_PUBLIC_LOAD_CONFIG
                      ).href
                    }
                    whatFor="course"
                  />
                ))}
              {isAppHirist && (
                <HiristCourseFeed
                  title=" "
                  titleProps={{fontWeight: 700}}
                  showFiltersAndViewAllCoursesText={false}
                  showAllCourses
                  coursesArr={
                    mapCourseCategoryDataToCourse(category.data) || []
                  }
                  handleEnquiryInLoggedOutState={handleEnquiryInLoggedOutState}
                />
              )}
            </Card>
          );
        })}
      </Grid>
      {isDeviceLarge && isAppIimjobs && (
        <Grid item xxs={12} md={3}>
          <AdsPanelHarness
            variant={VersionVariants.VERSION_TWO}
            showSmallBadges={false}
            showLearning
            showFeatured={false}
            showLearningCenterCTA={false}
            showLearnGrow={false}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default List;
